/* ashed.in — turntable scrub + wordmark journey
   One fixed element carries the wordmark through the whole flow:
   drops from the top of the screen to rest at its bottom while the
   turntable spins (the lit table beneath is what turns it black), then
   stays stuck to the image's bottom edge and exits with it — never
   entering the white. The nav bar appears once the hero has passed. */
(() => {
  const FRAME_COUNT = 181;
  const TITLE_TOP_VH = 0.12;   // resting position of the wordmark (fraction of viewport)
  const BOUNDARY_PAD = 0.06;   // gap the wordmark keeps above the screen/image bottom
  const NAV_REVEAL = 0.18;     // nav bar fades in over the last stretch of the heading's exit
  const DROP_SPAN = 0.84;      // the drop finishes this far into the pin, then the letters hold
  const TURN_ROUND = 0.09;     // radius on the hold→ride-out corner, as a fraction of the viewport
  const DARK_LUMA = 95;        // backdrop luminance that reads as "dark" → silver letters
  const LIGHT_LUMA = 165;      // …and as "light" → charcoal letters

  const canvas = document.getElementById('turntable');
  const ctx = canvas.getContext('2d');
  const stage = document.getElementById('stage');
  const fallTitle = document.getElementById('fallTitle');
  const fallDark = document.getElementById('fallDark');
  const navWordmark = document.getElementById('navWordmark');
  const paperNav = document.querySelector('.paper-nav');
  const paper = document.getElementById('products');
  const progressFill = document.getElementById('progressFill');
  const heroMeta = document.querySelector('.hero-meta');
  const loadBadge = document.getElementById('loadBadge');
  const loadPct = document.getElementById('loadPct');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // phones show only the centre strip of the wide frames, so they get the
  // pre-cropped portrait set (assets/frames-p: same sharpness, ~40% fewer bytes)
  const PORTRAIT = window.innerHeight > window.innerWidth * 1.15;
  const FRAME_DIR = PORTRAIT ? '/assets/frames-p' : '/assets/frames';
  /* Shopify serves theme assets flat, with a version query per file, so the
     URLs cannot be built by index — the page emits the list instead. */
  const FRAME_LIST = PORTRAIT ? window.ASHED_FRAMES_P : window.ASHED_FRAMES;
  const framePath = i => (FRAME_LIST && FRAME_LIST[i])
    || `${FRAME_DIR}/frame-${String(i + 1).padStart(3, '0')}.webp`;
  const viewport = document.querySelector('.viewport');
  const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const frames = new Array(FRAME_COUNT).fill(null);
  const loading = new Set();
  let loadedCount = 0;

  // ---------- layout metrics ----------
  let vw = 0, vh = 0, releaseY = 0, paperTop = 0, stageTop = 0;
  let fallH = 0;

  // Geometry comes from the sticky viewport box, never window.innerHeight:
  // the CSS sizes it with lvh, so the phone URL bar showing/hiding changes
  // nothing here. (Measuring innerHeight re-laid the whole animation on
  // every bar toggle — cleared canvas, jumping wordmark.)
  function measure() {
    vw = viewport.clientWidth || window.innerWidth;
    vh = viewport.clientHeight || window.innerHeight;
    stageTop = stage.offsetTop;
    releaseY = stage.offsetHeight - vh;
    const y = window.scrollY;
    paperTop = paper.getBoundingClientRect().top + y;

    // big wordmark geometry (offsetWidth/Height ignore transforms)
    fallH = fallTitle.offsetHeight;

    sizeCanvas();
    dirty = true;
  }

  function sizeCanvas() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(vw * dpr), h = Math.round(vh * dpr);
    if (canvas.width === w && canvas.height === h) return;
    canvas.width = w;
    canvas.height = h;
    // resizing wipes the bitmap — repaint at once so the hero is never blank
    if (drawnFrame >= 0 && frames[drawnFrame]) drawCover(frames[drawnFrame]);
  }

  // ---------- frame loading (priority queue, nearest-first) ----------
  const queue = Array.from({ length: FRAME_COUNT }, (_, i) => i);
  const CONCURRENCY = 8;

  function pump() {
    while (loading.size < CONCURRENCY && queue.length) {
      const i = queue.shift();
      if (frames[i] || loading.has(i)) continue;
      loading.add(i);
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        frames[i] = img;
        loading.delete(i);
        loadedCount++;
        const pct = Math.round((loadedCount / FRAME_COUNT) * 100);
        if (loadPct) loadPct.textContent = pct;
        if (loadBadge && loadedCount === FRAME_COUNT) loadBadge.classList.add('done');
        dirty = true;
        pump();
        wake();
      };
      img.onerror = () => { loading.delete(i); pump(); };
      img.src = framePath(i);
    }
  }

  function prioritize(i) {
    if (frames[i] || loading.has(i)) return;
    const pos = queue.indexOf(i);
    if (pos > 0) { queue.splice(pos, 1); queue.unshift(i); }
  }

  function nearestLoaded(i) {
    if (frames[i]) return i;
    for (let d = 1; d < FRAME_COUNT; d++) {
      if (i - d >= 0 && frames[i - d]) return i - d;
      if (i + d < FRAME_COUNT && frames[i + d]) return i + d;
    }
    return -1;
  }

  // ---------- drawing ----------
  let drawnFrame = -1, dirty = true;

  // A 12×1 probe: the wordmark's band is downsampled into it, so one
  // read gives the average brightness the letters are sitting on.
  // Cover-crop differs per viewport, so measuring beats guessing.
  const probe = document.createElement('canvas');
  probe.width = 12;
  probe.height = 1;
  const pctx = probe.getContext('2d', { willReadFrequently: true });

  function backdropLuma(bandTop, bandBottom, viewportBottom) {
    const dpr = canvas.width / vw;
    const bandH = Math.max(bandBottom - bandTop, 1);

    // whatever sits below the departing viewport is the paper section
    const paperFrac = clamp((bandBottom - viewportBottom) / bandH, 0, 1);
    const paperLuma = 246;
    if (paperFrac >= 1) return paperLuma;

    // the rest is video: map screen coords into the canvas, which has
    // travelled up by exactly the amount the viewport has scrolled away
    const shift = vh - viewportBottom;
    const sy = clamp((bandTop + shift) * dpr, 0, canvas.height - 1);
    const sh = clamp(bandH * dpr, 1, canvas.height - sy);
    const sw = Math.min(canvas.width, fallTitle.offsetWidth * dpr) || canvas.width;
    const sx = clamp((canvas.width - sw) / 2, 0, canvas.width - 1);

    let videoLuma = paperLuma;
    try {
      pctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, 12, 1);
      const d = pctx.getImageData(0, 0, 12, 1).data;
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += 0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2];
      }
      videoLuma = sum / 12;
    } catch (_) { /* nothing drawn yet — keep the light default */ }

    return lerp(videoLuma, paperLuma, paperFrac);
  }

  function drawCover(img) {
    const cw = canvas.width, ch = canvas.height;
    const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
    const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
    ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
  }

  // ---------- scrub + morph state ----------
  let current = 0; // smoothed frame position
  let firstTick = true; // snap (not lerp) on the first tick so mid-page reloads show the right frame
  let lastProgress = -1, lastMix = -1, lastTransform = '', lastNavP = -1;
  let mix = 0; // silver → charcoal, eased toward the measured backdrop
  let luma = 246, lastFallY = -1, lumaFrame = -2, lastBoundary = -1;

  // The loop only runs while something is changing (scroll, a frame
  // arriving, the easing settling); idle pages cost nothing.
  let running = false;
  function wake() {
    if (!running) { running = true; requestAnimationFrame(tick); }
  }

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, e) => a + (b - a) * e;
  const smooth = x => { x = clamp(x, 0, 1); return x * x * (3 - 2 * x); };
  // A minimum with a rounded corner. Math.min() cuts the corner where two
  // curves cross, which flips the wordmark's velocity in a single frame; this
  // blends across k px so the turn has a radius. It is always <= min(a, b),
  // so the letters still never cross below the image's bottom edge.
  const softMin = (a, b, k) => {
    if (k <= 0) return Math.min(a, b);
    const h = clamp(0.5 + 0.5 * (b - a) / k, 0, 1);
    return lerp(b, a, h) - k * h * (1 - h);
  };

  function tick() {
    // scroll relative to where the stage starts (the marquee sits above it)
    const y = Math.max(0, window.scrollY - stageTop);

    // -- frame target from scroll --
    // The rotation spans pin + handoff: its halfway point lands at the
    // release, so the wordmark starts moving mid-animation and the
    // turntable finishes behind the rising white.
    const scrubEnd = releaseY + vh;
    const p = scrubEnd > 0 ? clamp(y / scrubEnd, 0, 1) : 1;
    const target = p * (FRAME_COUNT - 1);
    prioritize(Math.round(target));

    if (firstTick) { current = target; firstTick = false; }
    current += (target - current) * 0.16;
    if (Math.abs(target - current) < 0.05) current = target;
    const frameIdx = nearestLoaded(Math.round(current));

    if ((frameIdx !== drawnFrame || dirty) && frameIdx >= 0) {
      drawCover(frames[frameIdx]);
      drawnFrame = frameIdx;
      dirty = false;
    }

    // -- progress hairline --
    if (progressFill && Math.abs(p - lastProgress) > 0.002) {
      progressFill.style.transform = `scaleX(${p})`;
      lastProgress = p;
    }

    // -- handoff: video slides away, light sweeps up behind the letters --
    const t = releaseY > 0 ? clamp((y - releaseY) / vh, 0, 1) : 0;

    // -- the drop, then the ride-out --
    // Act one (pinned): the wordmark glides from the top of the screen to
    // rest just above its bottom — over the lit tabletop, which is what
    // turns the letters black. Act two (handoff): the video's bottom edge
    // rises and the wordmark stays stuck to it, exiting with the image —
    // never entering the white space below.
    // The drop lands before the pin releases, so the letters are already at
    // rest when the image starts to move: they ride it out 1:1, rigidly,
    // instead of still falling and having to reverse (which read as a bounce).
    const d = releaseY > 0 ? smooth(y / (releaseY * DROP_SPAN)) : 1;
    const boundary = vh * (1 - t);
    // portrait frames are composed with the table strip at the very bottom,
    // so the letters sit lower there to land on it
    const pad = (vh > vw * 1.15 ? 0.03 : BOUNDARY_PAD) * vh;
    const topRest = TITLE_TOP_VH * vh;
    const bottomRest = vh - pad - fallH;
    // Where the drop meets the rising image edge, round the turn rather than
    // cut it: a hard min() brought the letters to a dead stop and then threw
    // them back up at full scroll speed — the bounce.
    const fallY = softMin(topRest + (bottomRest - topRest) * d,
                          boundary - pad - fallH, TURN_ROUND * vh);

    // meta row bows out as the letters arrive at the bottom
    if (heroMeta) heroMeta.style.opacity = String(1 - clamp((d - 0.5) / 0.3, 0, 1));
    const tf = `translate(-50%, ${fallY.toFixed(2)}px)`;
    if (tf !== lastTransform) {
      fallTitle.style.transform = tf;
      lastTransform = tf;
    }
    fallTitle.style.visibility = fallY + fallH <= 0 ? 'hidden' : 'visible';

    // gradual silver→charcoal crossfade, driven by the backdrop the
    // letters actually sit on — sampled at their current position
    // the readback (drawImage + getImageData) is the expensive part on
    // phones — only re-sample when the letters or the frame under them moved
    if (fallY !== lastFallY || drawnFrame !== lumaFrame || boundary !== lastBoundary) {
      luma = backdropLuma(fallY + fallH * 0.22, fallY + fallH * 0.78, boundary);
      lastFallY = fallY; lumaFrame = drawnFrame; lastBoundary = boundary;
    }
    // the composed portrait band is darker overall (the letters land in the
    // sculpture's table shadow, ~120-140 luma), so its thresholds sit lower
    const portrait = vh > vw * 1.15;
    const darkL = portrait ? 70 : DARK_LUMA;
    const lightL = portrait ? 122 : LIGHT_LUMA;
    const mixTarget = smooth((luma - darkL) / (lightL - darkL));
    mix += (mixTarget - mix) * 0.07;
    if (Math.abs(mix - lastMix) > 0.004) {
      fallDark.style.opacity = String(mix);
      lastMix = mix;
    }

    // -- nav bar + centred mark appear right as the heading passes out --
    const navP = clamp(1 - (fallY + fallH) / (NAV_REVEAL * vh), 0, 1);
    if (Math.abs(navP - lastNavP) > 0.004) {
      paperNav.style.setProperty('--bar', navP.toFixed(3));
      navWordmark.style.opacity = navP.toFixed(3);
      lastNavP = navP;
    }

    const settled = current === target && Math.abs(mixTarget - mix) < 0.002 && !dirty;
    if (settled) { running = false; return; }
    requestAnimationFrame(tick);
  }

  // ---------- reduced motion: static final frame, no morph ----------
  function initReduced() {
    measure();
    const img = new Image();
    img.onload = () => { frames[FRAME_COUNT - 1] = img; drawCover(img); };
    img.src = framePath(FRAME_COUNT - 1);
    paperNav.style.setProperty('--bar', '1');
    if (loadBadge) loadBadge.classList.add('done');
    window.addEventListener('resize', () => { measure(); if (frames[FRAME_COUNT - 1]) drawCover(frames[FRAME_COUNT - 1]); });
  }

  // ---------- boot ----------
  // Save-Data or a 2G/3G link: the static final frame, no 181-frame scrub
  const conn = navigator.connection || {};
  const slow = !!conn.saveData || /(^|[^4-9])[23]g$/.test(conn.effectiveType || '');
  if (reducedMotion || slow) {
    initReduced();
    return;
  }

  measure();
  let lastW = viewport.clientWidth, lastH = viewport.clientHeight;

  // debug: ?y=1234 jumps to a scroll position (screenshot QA)
  const dbgY = new URLSearchParams(location.search).get('y');
  if (dbgY !== null) window.scrollTo(0, parseFloat(dbgY));

  pump();
  wake();

  function onResize() {
    const w = viewport.clientWidth, h = viewport.clientHeight;
    if (w === lastW && h === lastH) return;
    // on phones a height-only change is the URL bar showing/hiding: the
    // lvh-sized hero didn't move, so leave the geometry alone
    if (isTouch && w === lastW) return;
    lastW = w; lastH = h;
    measure();
    wake();
  }
  window.addEventListener('resize', onResize);
  if (window.ResizeObserver) new ResizeObserver(onResize).observe(viewport);
  window.addEventListener('scroll', wake, { passive: true });
  window.addEventListener('orientationchange', () => setTimeout(onResize, 300));
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measure);
  }
})();
